import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { FileText, CheckCircle2, XCircle, Calendar, Users, QrCode, ExternalLink, Settings, Eye } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

const getStatusIcon = (status) => {
  switch (status) {
    case "open":
      return <CheckCircle2 className="h-4 w-4 text-secondary" />;
    case "closed":
      return <XCircle className="h-4 w-4 text-white/50" />;
    default:
      return <FileText className="h-4 w-4 text-white/50" />;
  }
};

const getStatusBadgeColor = (status) => {
  switch (status) {
    case "open":
      return "bg-secondary/20 text-secondary border border-secondary/30 hover:bg-secondary/30";
    case "closed":
      return "bg-white/10 text-white/70 border border-white/10 hover:bg-white/20";
    default:
      return "bg-white/10 text-white/70 border border-white/10 hover:bg-white/20";
  }
};

export function SurveysTab() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Helper function to determine survey status based on current date vs endDate
  const getSurveyStatus = (endDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    return today <= end ? "open" : "closed";
  };

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        const surveysQuery = query(collection(db, 'surveys'), orderBy('startDate', 'asc'));
        const querySnapshot = await getDocs(surveysQuery);

        const surveysData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            status: getSurveyStatus(data.endDate)
          };
        });

        setSurveys(surveysData);
      } catch (err) {
        console.error('Error fetching surveys:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSurveys();
  }, []);

  if (loading) return <div className="text-white text-center py-8">Loading surveys...</div>;
  if (error) return <div className="text-red-400 text-center py-8">Error loading surveys: {error}</div>;

  // Split surveys into active and completed
  const activeSurveys = surveys.filter(s => s.status === 'open');
  const completedSurveys = surveys.filter(s => s.status === 'closed');

  const totalSurveys = surveys.length;
  const activeCount = activeSurveys.length;
  const completedCount = completedSurveys.length;

  return (
    <div className="space-y-6">
      <div className="section-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="section-title">Community Surveys </h2>
            <p className="section-text">Your input is valuable to us. Participate in community surveys to help shape the masterplan.</p>
          </div>

          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {activeCount} Active
            </Badge>
            {user && (
              <Button
                onClick={() => navigate('/manage-surveys')}
                className="bg-primary-500 hover:bg-primary-600 text-white flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                Manage Surveys
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Top Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-black/40 border-white/10">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-secondary">
              <FileText className="h-4 w-4" />
              Total Surveys
            </CardDescription>
            <CardTitle className="text-3xl text-white">{totalSurveys}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-white/70">
              {activeCount} active, {completedCount} completed
            </p>
          </CardContent>
        </Card>

        <Card className="bg-black/40 border-white/10">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-secondary">
              <CheckCircle2 className="h-4 w-4" />
              Active Surveys
            </CardDescription>
            <CardTitle className="text-3xl text-white">{activeCount}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-white/70">
              Open for community participation
            </p>
          </CardContent>
        </Card>

        <Card className="bg-black/40 border-white/10">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-secondary">
              <Users className="h-4 w-4" />
              Completed Surveys
            </CardDescription>
            <CardTitle className="text-3xl text-white">{completedCount}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-white/70">
              Result Available for Review
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Active Surveys Section */}
      <Card className="bg-black/40 border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <CheckCircle2 className="h-5 w-5 text-secondary" />
            Active Surveys
          </CardTitle>
          <CardDescription className="text-white/70">
            Current surveys open for community participation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeSurveys.map((survey) => {
              // Calculate days closed before end date
              const endTime = new Date(survey.endDate).getTime();
              const currentTime = new Date().getTime();
              const daysClosed = Math.floor((endTime - currentTime) / (1000 * 60 * 60 * 24));

              return (
                <Card key={survey.id} className="bg-black/60 border-secondary/30 border-2">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(survey.status)}
                      <Badge className={getStatusBadgeColor(survey.status)}>
                        Open
                      </Badge>
                    </div>
                    <CardTitle className="text-lg text-white">{survey.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* QR Code */}
                    <div className="flex justify-center p-4 bg-white border-2 border-dashed border-secondary/30 rounded-lg">
                      <div className="w-32 h-32 flex items-center justify-center rounded overflow-hidden">
                        {survey.surveyQRImageURL ? (
                          <img
                            src={survey.surveyQRImageURL}
                            alt="Survey QR Code"
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              // Fallback to API-generated QR if custom image fails to load
                              e.target.src = `https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=${encodeURIComponent(survey.surveyURL)}`;
                            }}
                          />
                        ) : survey.surveyURL ? (
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=${encodeURIComponent(survey.surveyURL)}`}
                            alt="Survey QR Code"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <QrCode className="h-20 w-20 text-secondary" />
                        )}
                      </div>
                    </div>

                    {/* Survey Info */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/70">Approximate Time:</span>
                        <span className="font-medium text-white">~{survey.approximateTime} mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Questions:</span>
                        <span className="font-medium text-white">{survey.questions}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Status:</span>
                        <span className="font-medium text-secondary">Open</span>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-center gap-1 text-sm text-white/70 pt-2 border-t border-white/10">
                      <Calendar className="h-4 w-4" />
                      <span>Closes in {daysClosed} days</span>
                    </div>

                    {/* Take Survey Button */}
                    <Button
                      className="w-full gap-1 bg-secondary text-black hover:bg-secondary/90 font-semibold"
                      onClick={() => {
                        if (survey.surveyURL) {
                          window.open(survey.surveyURL, '_blank');
                        }
                      }}
                    >
                      Take Survey
                      <ExternalLink className="h-3 w-3" />
                    </Button>

                    {/* View Survey Result Button - Hidden 
                    <Button 
                      className={`w-full gap-1 ${
                        survey.surveyResultURL && survey.surveyResultURL.trim() !== ""
                          ? "border border-secondary/50 text-secondary hover:bg-secondary/10 hover:text-secondary"
                          : "border border-white/50 text-white cursor-not-allowed hover:bg-white/5"
                      }`}
                      variant="outline"
                      disabled={!survey.surveyResultURL || survey.surveyResultURL.trim() === ""}
                      onClick={() => {
                        if (survey.surveyURL) {
                          window.open(survey.surveyResultURL, '_blank');
                        }
                      }}
                    >
                      {survey.surveyResultURL && survey.surveyResultURL.trim() !== "" ? (
                        <>
                          View Result
                          <ExternalLink className="h-3 w-3" />
                        </>
                      ) : (
                        "Result Unavailable"
                      )}
                    </Button>
                    */}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Completed Surveys Section */}
      <Card className="bg-black/40 border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <XCircle className="h-5 w-5 text-white/50" />
            Completed Surveys
          </CardTitle>
          <CardDescription className="text-white/70">
            Previous Surveys
          </CardDescription>
        </CardHeader>
        <CardContent>
          {completedSurveys.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-white/70">No completed surveys yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {completedSurveys.map((survey) => {
                const hasResults = survey.surveyResultURL && survey.surveyResultURL.trim() !== "";

                return (
                  <Card key={survey.id} className="bg-black/30 border-white/10 opacity-80">
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon(survey.status)}
                        <Badge className={getStatusBadgeColor(survey.status)}>
                          Closed
                        </Badge>
                      </div>
                      <CardTitle className="text-lg text-white">{survey.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* QR Code (Disabled for closed surveys) */}
                      <div className="flex justify-center p-4 bg-black/40 border-2 border-dashed border-white/10 rounded-lg">
                        <div className="w-32 h-32 flex items-center justify-center rounded overflow-hidden">
                          {survey.surveyQRImageURL ? (
                            <img
                              src={survey.surveyQRImageURL}
                              alt="Survey QR Code"
                              className="w-full h-full object-contain opacity-50"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<svg class="h-20 w-20 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"></path></svg>';
                              }}
                            />
                          ) : (
                            <QrCode className="h-20 w-20 text-white/30" />
                          )}
                        </div>
                      </div>

                      {/* Survey Info */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white/70">Questions:</span>
                          <span className="font-medium text-white">{survey.questions}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/70">Responses:</span>
                          <span className="font-medium text-white">{survey.responses || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/70">Status:</span>
                          <span className="font-medium text-white/50">Closed</span>
                        </div>
                      </div>

                      {/* Date */}
                      <div className="flex items-center gap-1 text-sm text-white/70 pt-2 border-t border-white/10">
                        <Calendar className="h-4 w-4" />
                        <span>Closed: {new Date(survey.endDate).toLocaleDateString()}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Survey Results Section */}
      <Card className="bg-black/40 border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Eye className="h-5 w-5 text-secondary" />
            First Survey Result
          </CardTitle>
          <CardDescription className="text-white/70">
            View First Survey Result
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <Button
              className="gap-2 bg-secondary text-black hover:bg-secondary/90 font-semibold px-6 py-4"
              onClick={() => {
                window.open('https://lookerstudio.google.com/u/0/reporting/ea20a220-1bfa-4af0-8897-0ffe187a35db/page/ILTdF', '_blank');
              }}
            >
              View First Survey Result
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Call to Action */}
      <Card className="bg-black/60 border-secondary/30">
        <CardHeader>
          <CardTitle className="text-white">Your Voice Matters</CardTitle>
          <CardDescription className="text-secondary">Help shape the future of Miner's Park</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm mb-4 text-white/80">
            Community input is essential to creating a park master plan that truly serves everyone.
            Please take a few minutes to complete our active surveys and share your thoughts.
            Your feedback directly influences the final design that will be presented to council.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
